<script setup lang="ts">
import { computed } from "vue";
import { Badge, Button, Card, Icon, Input, Select, StatTile, Tag } from "@/design-system";
import { BOTTLE_CHOICES, type Sport } from "@/domain/legs";
import type { Plan } from "@/composables/usePlan";

/**
 * Step 1 -- Rennen & Etappen.
 *
 * Follows the component mapping in docs/handover/reference/MvpSetup.jsx, which
 * the README names as the brand reference: a Select for the discipline with the
 * leg chips in the same card, and Inputs for the numbers.
 *
 * That is also what makes it fit. The wireframe's four radio pills and its
 * separate leg row cost 364px between them, against 844px of phone; the Select
 * version is a third of that, and the whole step now sits on one screen instead
 * of 1341px of scrolling.
 */
const props = defineProps<{ plan: Plan }>();
const emit = defineEmits<{ next: [] }>();

const p = props.plan;

const sportOptions = computed<{ value: Sport; label: string }[]>(() => [
  { value: "bike", label: p.t.value.sportBike },
  { value: "run", label: p.t.value.sportRun },
  { value: "triathlon", label: p.t.value.sportTri },
  { value: "duathlon", label: p.t.value.sportDu },
]);

function onNext() {
  if (p.advance() === "next-step") emit("next");
}
</script>

<template>
  <div class="race">
    <!-- Discipline and its legs, one card (MvpSetup.jsx). -->
    <Card :title="p.t.value.disciplineQ">
      <Select
        :model-value="p.sport.value"
        :options="sportOptions"
        data-testid="sport"
        @update:model-value="(v) => p.setSport(v as Sport)"
      />
      <div class="race__chips">
        <Tag
          v-for="l in p.labels.value"
          :key="l.key"
          selectable
          :selected="l.current"
          :icon="l.icon"
          :data-leg="l.key"
          @click="p.selectLeg(l.index)"
        >
          {{ l.name }}
        </Tag>
      </div>
    </Card>

    <!-- Weight beside the derived ceiling. -->
    <Card :title="p.t.value.weight">
      <div class="race__pair">
        <Input
          :model-value="p.weight.value"
          :label="p.t.value.weightField"
          suffix="kg"
          inputmode="numeric"
          data-testid="weight"
          @update:model-value="(v) => p.setWeight(Number(v))"
        />
        <StatTile
          :label="p.t.value.ceilingShort"
          :value="`${p.ceiling.value.min}–${p.ceiling.value.max}`"
          unit="g/h"
          variant="sunken"
          size="sm"
          data-testid="ceiling"
        />
      </div>
    </Card>

    <!-- Duration range for the active leg. -->
    <Card :title="p.t.value.duration">
      <template #action>
        <Badge tone="accent">
          {{ p.current.value.stage }}
        </Badge>
      </template>
      <div class="race__pair">
        <Input
          :model-value="p.leg.value.durMinMin"
          :label="p.t.value.from"
          :suffix="p.t.value.minutes"
          inputmode="numeric"
          data-testid="dur-min"
          @update:model-value="(v) => p.setDuration('durMinMin', Number(v))"
        />
        <Input
          :model-value="p.leg.value.durMinMax"
          :label="p.t.value.to"
          :suffix="p.t.value.minutes"
          inputmode="numeric"
          v-bind="p.durationError.value ? { error: p.durationError.value } : {}"
          data-testid="dur-max"
          @update:model-value="(v) => p.setDuration('durMinMax', Number(v))"
        />
      </div>
    </Card>

    <!-- Exactly one leg-specific block. -->
    <Card
      v-if="p.legType.value === 'swim'"
      variant="sunken"
      data-testid="leg-swim"
    >
      <div class="race__note">
        <Icon
          name="waves"
          :size="20"
          color="var(--fuel-fluid)"
        />
        <p>{{ p.t.value.swimHint }}</p>
      </div>
    </Card>

    <Card
      v-else-if="p.legType.value === 'run'"
      :title="p.t.value.cup"
      data-testid="leg-run"
    >
      <div class="race__pair">
        <Input
          :model-value="p.leg.value.cupMl"
          :label="p.t.value.cupField"
          suffix="ml"
          inputmode="numeric"
          @update:model-value="(v) => p.setVessel('cupMl', Number(v))"
        />
        <Input
          :model-value="p.leg.value.stations"
          :label="p.t.value.stationsShort"
          suffix="Stk"
          inputmode="numeric"
          @update:model-value="(v) => p.setVessel('stations', Number(v))"
        />
      </div>
    </Card>

    <Card
      v-else
      :title="p.t.value.bottle"
      data-testid="leg-bike"
    >
      <div class="race__chips">
        <Tag
          v-for="ml in BOTTLE_CHOICES"
          :key="ml"
          selectable
          :selected="p.leg.value.bottleMl === ml"
          :data-bottle="ml"
          @click="p.setVessel('bottleMl', ml)"
        >
          {{ ml }} ml
        </Tag>
      </div>
    </Card>

    <div
      class="race__footer app-actions"
      :class="{ 'race__footer--single': p.legIndex.value === 0 }"
    >
      <Button
        v-if="p.legIndex.value > 0"
        variant="outline"
        size="lg"
        icon="arrow-left"
        data-testid="back"
        @click="p.retreat()"
      >
        {{ p.t.value.back }}
      </Button>
      <Button
        size="lg"
        block
        icon-after="arrow-right"
        :disabled="!p.canAdvance.value"
        data-testid="next"
        @click="onNext"
      >
        {{ p.nextLabel.value }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.race {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}
.race__chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-top: var(--space-5);
}
.race__pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-5);
  align-items: end;
}
.race__note {
  display: flex;
  gap: var(--space-5);
  align-items: flex-start;
}
.race__note p {
  margin: 0;
  font: var(--type-body-sm);
  color: var(--text-body);
}
.race__footer {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--space-5);
}
.race__footer--single {
  grid-template-columns: minmax(0, 1fr);
}
/* The bottle chips card has no second row, so it needs no top margin. */
[data-testid="leg-bike"] .race__chips {
  margin-top: 0;
}
</style>
